import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import {
	LayoutDashboard,
	Monitor,
	Package,
	ShoppingCart,
	Users,
	BarChart3,
	ChevronRight,
	ChevronLeft,
	Tag,
	Shield,
	Bell,
	ChevronDown,
	HelpCircle,
	CreditCard,
	Percent,
	Building,
	CornerDownLeft,
	Puzzle,
	History,
	CornerDownRight,
} from "lucide-react";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import type { User } from "@/types";
import type { LucideIcon } from "lucide-react";
import { userHasPermission } from "@/lib/user-permissions";
interface SidebarProps {
	isCollapsed: boolean;
	onNavigate?: () => void;
}

interface NavItemBase {
	titleKey: string;
	icon: LucideIcon;
	permission: string | string[];
}
interface NavLink extends NavItemBase {
	href: string;
	children?: never;
}
interface NavGroup extends NavItemBase {
	href?: never;
	children: {
		titleKey: string;
		href: string;
		icon: LucideIcon;
		permission: string | string[];
	}[];
}
type NavItem = NavLink | NavGroup;

function canAccess(user: User | null, permission: string | string[]): boolean {
	return userHasPermission(user, permission);
}

export default function Sidebar({ isCollapsed, onNavigate }: SidebarProps) {
	const { t } = useTranslation();
	const location = useLocation();
	const { isRTL } = useLanguage();
	const { user } = useAuth();
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
	const [popoverGroup, setPopoverGroup] = useState<string | null>(null);

	const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

	const navConfig = useMemo<NavItem[]>(
		() => [
			{ titleKey: "navigation.dashboard", href: "/", icon: LayoutDashboard, permission: "dashboard.read" },
			{
				titleKey: "navigation.pointsOfSale",
				href: "/pos/list",
				icon: Monitor,
				permission: "pos.read",
			},
			{
				titleKey: "navigation.sales",
				href: "/sales",
				icon: ShoppingCart,
				permission: ["sales.read", "returns.read", "purchases.read"],
			},
			{
				titleKey: "navigation.products",
				href: "/products",
				icon: Package,
				permission: ["inventory.read", "products.read", "products.readCrossBranch", "stockTransfers.read"],
			},
			{
				titleKey: "navigation.contacts",
				href: "/contacts",
				icon: Users,
				permission: ["customers.read", "suppliers.read"],
			},
			{
				titleKey: "navigation.expenses",
				href: "/expenses",
				icon: CreditCard,
				permission: "expenses.read",
			},
			{ titleKey: "navigation.reports", href: "/reports", icon: BarChart3, permission: "reports.read" },
			{
				titleKey: "navigation.administration",
				href: "/management",
				icon: Shield,
				permission: ["users.read", "departments.read", "roles.read"],
			},
			{
				titleKey: "navigation.storeData",
				href: "/company",
				icon: Building,
				permission: ["company.read", "branches.read"],
			},
			{
				titleKey: "navigation.paymentsAndTaxes",
				href: "/payments",
				icon: Percent,
				permission: ["paymentMethods.read", "taxes.read", "settings.read"],
			},
			{
				titleKey: "navigation.checkoutPromotions",
				href: "/checkout",
				icon: Tag,
				permission: [
					"cashierDiscount.read",
					"cashierGiftCard.read",
					"cashierStoreCredit.read",
					"cashierLoyalty.read",
				],
			},
			{
				titleKey: "navigation.integrations",
				href: "/settings/integrations",
				icon: Puzzle,
				permission: "settings.read",
			},
			{
				titleKey: "navigation.notificationInbox",
				href: "/notifications",
				icon: Bell,
				permission: "notifications.read",
			},
			{ titleKey: "navigation.help", href: "/help", icon: HelpCircle, permission: "dashboard.read" },
			{ titleKey: "navigation.auditLog", href: "/audit", icon: History, permission: "audit.read" },
		],
		[],
	);

	const navigation = useMemo(() => {
		return navConfig
			.map((item): NavItem | null => {
				if (item.children) {
					if (!canAccess(user, item.permission)) return null;
					const visibleChildren = item.children.filter(c => canAccess(user, c.permission));
					if (visibleChildren.length === 0) return null;
					return { ...item, children: visibleChildren };
				}
				return canAccess(user, item.permission) ? item : null;
			})
			.filter((item): item is NavItem => item != null);
	}, [navConfig, user]);

	const isActive = (href: string) => {
		if (href === "/") {
			return location.pathname === "/";
		}
		const ancestors: Record<string, string[]> = {
			"/sales": ["/purchases", "/returns", "/quotations", "/invoices"],
			"/management": ["/users", "/roles", "/departments"],
			"/products": ["/recipes", "/stock-transfers"],
			"/pos/list": ["/pos"],
		};
		return (
			location.pathname === href ||
			location.pathname.startsWith(`${href}/`) ||
			ancestors[href]?.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ||
			false
		);
	};

	const toggleGroup = (groupKey: string) => {
		if (isCollapsed) {
			setPopoverGroup(prev => (prev === groupKey ? null : groupKey));
			return;
		}

		// only one item can be expanded at a time
		setExpandedGroups(prev => {
			let newSet = new Set(prev);
			if (newSet.has(groupKey)) {
				newSet = new Set([]);
			} else {
				newSet = new Set([groupKey]);
			}
			return newSet;
		});
	};

	useEffect(() => {
		setPopoverGroup(null);
	}, [isCollapsed, location.pathname]);

	useEffect(() => {
		if (!popoverGroup) return undefined;

		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setPopoverGroup(null);
			}
		};

		window.addEventListener("keydown", closeOnEscape);
		return () => window.removeEventListener("keydown", closeOnEscape);
	}, [popoverGroup]);

	// Auto-expand groups that contain active children
	useEffect(() => {
		const checkActiveChild = (children: NavGroup["children"]) => {
			return children.some(child => {
				if (child.href === "/") {
					return location.pathname === "/";
				}
				return location.pathname.startsWith(child.href);
			});
		};

		const groupsToExpand = new Set<string>();
		navigation.forEach(item => {
			if (item.children && checkActiveChild(item.children)) {
				groupsToExpand.add(item.titleKey);
			}
		});
		if (groupsToExpand.size > 0) {
			setExpandedGroups(prev => {
				const merged = new Set(prev);
				groupsToExpand.forEach(key => merged.add(key));
				return merged;
			});
		}
	}, [location.pathname, navigation]);

	const renderNavItem = (item: NavItem, index: number) => {
		const Icon = item.icon;
		const isNotifications = !item.children && item.href === "/notifications";

		// If item has children, render as expandable group
		if (item.children) {
			const groupKey = item.titleKey;
			const isExpanded = expandedGroups.has(groupKey);
			const isPopoverOpen = popoverGroup === groupKey;
			const panelId = `${groupKey}-panel`;

			return (
				<div key={groupKey + index.toString()} className="space-y-1 relative">
					<div className="px-4">
						<button
							type="button"
							onClick={() => toggleGroup(groupKey)}
							aria-expanded={isCollapsed ? isPopoverOpen : isExpanded}
							aria-controls={panelId}
							title={isCollapsed ? t(item.titleKey) : undefined}
							className="w-full flex items-center justify-between px-3 py-3 text-md font-medium rounded-md border border-white border-b-1 border-b-gray-100 transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
						>
							<div className="flex items-center">
								{Icon && <Icon className="h-5 w-5 me-3" />}
								{!isCollapsed && <span>{t(item.titleKey)}</span>}
							</div>
							{!isCollapsed &&
								(isExpanded ? (
									<ChevronDown className="h-4 w-4" />
								) : (
									<ChevronIcon className="h-4 w-4" />
								))}
						</button>
					</div>

					{isCollapsed && isPopoverOpen && (
						<div
							id={panelId}
							role="region"
							aria-label={t(item.titleKey)}
							className={cn(
								"absolute top-0 z-20 min-w-56 rounded-md border bg-background shadow-lg p-2",
								isRTL ? "right-full mr-2" : "left-full ml-2",
							)}
						>
							<div className="space-y-1">
								{item.children.map((child, childIndex: number) => {
									const ChildIcon = child.icon;
									const isChildActive = isActive(child.href);

									return (
										<Link
											key={child.href + childIndex.toString()}
											to={child.href}
											onClick={() => {
												setPopoverGroup(null);
												onNavigate?.();
											}}
											aria-current={isChildActive ? "page" : undefined}
											className={cn(
												"flex items-center px-3 py-3 text-sm font-medium rounded-md transition-colors",
												isChildActive
													? "border-s-2 border-primary bg-secondary text-primary font-bold"
													: "text-muted-foreground hover:text-foreground hover:bg-muted",
											)}
										>
											{ChildIcon && <ChildIcon className="h-4 w-4 me-2" />}
											<span>{t(child.titleKey)}</span>
										</Link>
									);
								})}
							</div>
						</div>
					)}
					{!isCollapsed && (
						<div
							id={panelId}
							role="region"
							aria-label={t(item.titleKey)}
							className={cn(
								"grid transition-[grid-template-rows] duration-300 ease-in-out",
								isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
							)}
						>
							<div className="min-h-0 overflow-hidden">
								<div className="space-y-1 bg-muted py-2">
									{item.children.map((child, childIndex: number) => {
										const ChildIcon = child.icon;
										const isChildActive = isActive(child.href);

										return (
											<Link
												key={child.href + childIndex.toString()}
												to={child.href}
												onClick={onNavigate}
												aria-current={isChildActive ? "page" : undefined}
												className={cn(
													"flex items-center px-3 py-3 text-sm font-medium rounded-md transition-colors",
													isChildActive
														? "border-s-2 border-primary bg-secondary text-primary font-bold mx-3 px-0"
														: "text-muted-foreground hover:text-foreground hover:bg-muted mx-3 px-0",
												)}
											>
												{isRTL ? (
													<CornerDownLeft className="h-4 w-4 me-2 ms-6" />
												) : (
													<CornerDownRight className="h-4 w-4 me-2 ms-6" />
												)}
												{ChildIcon && <ChildIcon className="h-4 w-4 me-3" />}
												<span>{t(child.titleKey)}</span>
											</Link>
										);
									})}
								</div>
							</div>
						</div>
					)}
				</div>
			);
		}

		// Regular navigation item
		const isItemActive = isActive(item.href);

		return (
			<div className="px-4" key={item.href + index.toString()}>
				<Link
					to={item.href}
					onClick={onNavigate}
					title={isCollapsed ? t(item.titleKey) : undefined}
					aria-current={isItemActive ? "page" : undefined}
					className={cn(
						`${isNotifications ? "" : ""} flex items-center min-h-11 px-3 py-2 text-sm font-medium rounded-lg transition-colors`,
						isItemActive
							? "border-s-2 border-primary bg-secondary text-primary font-bold"
							: "text-muted-foreground hover:text-foreground hover:bg-muted",
					)}
				>
					{Icon && <Icon className="h-5 w-5 me-3" />}
					{!isCollapsed && <span>{t(item.titleKey)}</span>}
				</Link>
			</div>
		);
	};

	return (
		<nav aria-label={t("navigation.main")} className="flex-1 max-h-[calc(100dvh-64px)] overflow-y-auto">
			{[
				{
					key: "daily",
					paths: ["/", "/pos/list", "/sales", "/products", "/contacts", "/expenses", "/reports"],
				},
				{
					key: "manage",
					paths: ["/management", "/company", "/payments", "/checkout", "/settings/integrations"],
				},
				{ key: "support", paths: ["/notifications", "/help", "/audit"] },
			].map(group => {
				const items = navigation.filter(item => item.href && group.paths.includes(item.href));
				return (
					items.length > 0 && (
						<div key={group.key} className="space-y-1 py-2">
							{items.map(renderNavItem)}
						</div>
					)
				);
			})}
		</nav>
	);
}
